import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Modal } from "../../components/modal";
import { UserForm, UserFormValues } from "../../components/user-form";
import { useGetAuthContext } from "../../queries/get-auth-context";
import { createAssociationUser } from "../../services/association-user";
import { navigateBack } from "../../utils/navigate-back";

const emptyFormValues: UserFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  sendInvite: true,
  isOwner: false,
  electedRepresentativeType: "",
  password: "",
};

export function UserCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: authContext } = useGetAuthContext();
  const isOwner =
    authContext?.type === "associationUser" && authContext.isOwner;
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formValues, setFormValues] = useState<UserFormValues>(emptyFormValues);

  const closeModal = () => {
    navigateBack(navigate, "/admin/user/list");
  };

  if (authContext && !isOwner) {
    return <Navigate replace to="/admin/user/list" />;
  }

  return (
    <Modal open onClose={closeModal}>
      <UserForm
        hasError={hasError}
        loading={loading}
        mode="create"
        values={formValues}
        onCancel={closeModal}
        onChange={setFormValues}
        onSubmit={() => {
          void (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await createAssociationUser({
                email: formValues.email,
                ...(formValues.firstName.trim()
                  ? { firstName: formValues.firstName.trim() }
                  : {}),
                ...(formValues.lastName.trim()
                  ? { lastName: formValues.lastName.trim() }
                  : {}),
                sendInvite: formValues.sendInvite,
                isOwner: formValues.isOwner,
                ...(formValues.electedRepresentativeType
                  ? {
                      electedRepresentativeType:
                        formValues.electedRepresentativeType,
                    }
                  : {}),
                ...(formValues.sendInvite
                  ? {}
                  : { password: formValues.password }),
              });
              await queryClient.invalidateQueries({
                queryKey: ["associationMembers"],
              });
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
